import type { ClientConfig } from './ClientConfig';
import { trackErrorBlacklist } from './config';
import HttpError from './HttpError';
import ResponseParseError from './ResponseParseError';
import type { OptionsStrict } from './types';

export async function handleResponse(
  response: Response,
  request: Request,
  options: OptionsStrict,
  config: ClientConfig
) {
  verifyAuthenticated(response, config);
  verifyResponse(response, request, config);
  return parseResponse(response, options, config);
}

function verifyAuthenticated(response: Response, config: ClientConfig) {
  if (response.status === 401) {
    config.onUnauthorized?.();
  }
}

function verifyResponse(
  response: Response,
  request: Request,
  config: ClientConfig
) {
  if (!response.ok) {
    const error = new HttpError(response, request);
    if (!trackErrorBlacklist.includes(response.status)) {
      config.onError?.(error);
    }
    throw error;
  }
}

async function parseResponse(
  response: Response,
  options: OptionsStrict,
  config: ClientConfig
) {
  try {
    switch (options.responseType) {
      case 'blob':
        return await response.blob();
      case 'raw':
        return response;
      case 'text':
        return await response.text();
      case 'void':
        return undefined;
      case 'json':
      default:
        return await parseJson(response, options);
    }
  } catch (cause) {
    const error = new ResponseParseError(response, cause);
    config.onParseError?.(error);
    throw error;
  }
}

async function parseJson(response: Response, options: OptionsStrict) {
  const json = await response.json();
  const data = options.selector(json, options);
  return attachOriginal(data, json);
}

function attachOriginal(data: any, json: any) {
  const copy = Array.isArray(data) ? [...data] : { ...data };
  copy.__original__ = json;
  return copy;
}
